import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from '@/components/common/Loader';
import { PageHeader } from '@/components/common/PageHeader';
import { useProfesional } from '@/features/profesionales/hooks/useProfesionales';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';
import type { CanalEnvio } from '@/types/enums';

import { CambiarProfesionalDialog } from '../components/CambiarProfesionalDialog';
import { TelefonosContactoDisplay } from '../components/TelefonosContactoDisplay';
import { EstablecimientoForm } from '../components/EstablecimientoForm';
import {
  useEstablecimiento,
  useUpdateEstablecimiento,
} from '../hooks/useEstablecimientos';
import type { EstablecimientoUpdateFormValues } from '../schemas';
import type { EstablecimientoUpdate } from '../types';

export function EditarEstablecimientoPage(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const { uidEstablecimiento } = useParams<{ uidEstablecimiento: string }>();
  const [cambiarProfDialogOpen, setCambiarProfDialogOpen] = useState(false);

  const {
    data: establecimiento,
    isLoading,
    isError,
    error,
  } = useEstablecimiento(uidEstablecimiento);

  const { data: profesionalActual } = useProfesional(
    establecimiento?.uid_profesional_actual
  );

  const updateMutation = useUpdateEstablecimiento();

  async function handleSubmit(values: EstablecimientoUpdateFormValues): Promise<void> {
    if (!uidEstablecimiento) return;

    const fac = values.facturacion;
    const facturacionFilled =
      fac.cuit ||
      fac.razon_social ||
      fac.email_facturacion ||
      fac.telefono_whatsapp_facturacion ||
      fac.envio_factura;

    const payload: EstablecimientoUpdate = {
      nombre: values.nombre.trim(),
      firma_ganaderos: values.firma_ganaderos?.trim() || null,
      localidad: values.localidad?.trim() || null,
      facturacion: facturacionFilled
        ? {
            cuit: fac.cuit?.trim() || null,
            razon_social: fac.razon_social?.trim() || null,
            email_facturacion: fac.email_facturacion?.trim() || null,
            telefono_whatsapp_facturacion:
              fac.telefono_whatsapp_facturacion?.trim() || null,
            envio_factura: fac.envio_factura ? (fac.envio_factura as CanalEnvio) : null,
          }
        : null,
      telefonos_contacto: (values.telefonos_contacto ?? [])
        .filter((t) => t.numero.trim())
        .map((t) => ({ numero: t.numero, nombre: t.nombre?.trim() || null })),
    };

    try {
      const updated = await updateMutation.mutateAsync({
        uid: uidEstablecimiento,
        data: payload,
      });
      notify.success(`Cambios guardados en "${updated.nombre}".`);
    } catch {
      // Error in form
    }
  }

  if (isLoading) {
    return <Loader message="Cargando establecimiento..." />;
  }

  if (isError || !establecimiento) {
    const isNotFound = isApiError(error) && error.isNotFound();
    return (
      <>
        <PageHeader title="Editar establecimiento" />
        <Alert severity="error">
          {isNotFound
            ? 'El establecimiento no existe o fue eliminado.'
            : error instanceof Error
              ? error.message
              : 'No se pudo cargar el establecimiento.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/establecimientos')}
          sx={{ mt: 2 }}
        >
          Volver al listado
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={establecimiento.nombre}
        subtitle="Editar establecimiento"
        action={
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/establecimientos')}
          >
            Volver
          </Button>
        }
      />

      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Profesional asignado
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profesionalActual?.nombre_apellido ?? 'Cargando...'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => setCambiarProfDialogOpen(true)}
                >
                  Cambiar profesional
                </Button>
              </Stack>

              {(establecimiento.telefonos_contacto?.length ?? 0) > 0 && (
                <>
                  <Divider />
                  <TelefonosContactoDisplay telefonos={establecimiento.telefonos_contacto} />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <EstablecimientoForm
              mode="edit"
              defaultValues={establecimiento}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/establecimientos')}
              submitLabel="Guardar cambios"
              submitting={updateMutation.isPending}
              error={updateMutation.error}
            />
          </CardContent>
        </Card>
      </Stack>

      {cambiarProfDialogOpen && (
        <CambiarProfesionalDialog
          open={true}
          establecimiento={establecimiento}
          profesionalActualNombre={
            profesionalActual?.nombre_apellido ?? 'el profesional actual'
          }
          onClose={() => setCambiarProfDialogOpen(false)}
          onSuccess={() => {
            setCambiarProfDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
